import * as d3 from 'd3';
import { addMinutes, differenceInMilliseconds, differenceInSeconds, isBefore } from 'date-fns';
import { compact, filter, map, maxBy, minBy, sortedUniq, stubTrue, uniq } from 'lodash';
import { ReactNode } from 'react';

import { UserDisplayPreferences } from '@/api/userAccount';
import {
    captureChartTeardown,
    createTimelineTemplate,
    frameCoalescedReporter,
    pointerGestureReporter,
} from '@/charts/common';
import { GenericObject, findRelativeAncestor, getXValue, getYValue, positionCursorTooltip } from '@/charts/helpers';
import { MarginConfig, PointerInteractionSource } from '@/charts/types';
import { formatTimestamp } from '@/components/data/Timestamp';
import { DateRange } from '@/store';
import { isFiniteNumber } from '@/utils';

export const DOT_HEIGHT = 16;

/**
 * We'll consider a timestamp "close enough" to count as a given hover/selection if it's within this distance
 */
const TIMESTAMP_WINDOW_PIXELS = 4;

export type Timeline<D> = {
    id: string;
    seriesLabel: string;
    valueKey?: keyof D extends string ? keyof D : string;
    valueLabel: string;
    valueRange: { valueMin: number; valueMax: number };
    dataFilter?: (datum: D) => boolean;
    observationIntervals?: DateRange[];
    datumDetailRenderer?: (datum: D, line: Timeline<D>) => ReactNode;
    color: string;
};

export type TimelineDataSlice<D, L extends Timeline<D> = Timeline<D>> = { datum: D; lines: L[] }[];

type RenderTemplateResult = NonNullable<ReturnType<typeof createTimelineTemplate>>;

export class TimelineChart<Datum extends GenericObject, L extends Timeline<Datum> = Timeline<Datum>> {
    protected readonly dataset: Datum[];
    public readonly allTimestamps: string[];
    protected readonly lines: (L & { dataFilter: NonNullable<L['dataFilter']> })[];

    protected readonly onSelectTimestamp: (timestamp: string | null) => void;

    private removeOwnedNodes: (() => void) | null = null;
    private expansionBracketTarget: { xTimeScale: d3.ScaleTime<number, number, never> } | undefined = undefined;
    private maybeClearingSelectedTimestamp: string | null = null;
    private selectedTimestamp: Date | null = null;
    private readonly draggedTimestamp = frameCoalescedReporter<string>((timestamp) =>
        this.onSelectTimestamp(timestamp)
    );
    private readonly pointerGesture = pointerGestureReporter((source) => this.options.onUserInteraction?.(source));

    protected rendered?: RenderTemplateResult & {
        relativeAncestor: Element;
        timestampWindowMs: number;
        legendSvg: d3.Selection<SVGGElement, unknown, HTMLElement, unknown> | null;
        dynamicElements?: ReturnType<RenderTemplateResult['addDynamicElements']>;
    };

    protected lineData: { [lineId: string]: Datum[] } = {};

    constructor(
        dataset: Datum[],
        lines: L[],
        protected readonly options: {
            legend: { label: string } | false;
            xAxisKey: keyof Datum extends string ? keyof Datum : string;
            xAxisEndKey?: keyof Datum extends string ? keyof Datum : string;
            chartWidth?: number;
            chartHeight?: number;
            margin?: Partial<MarginConfig>;
            dotHeight?: number;
            bandHeight?: number;
            selectTimeBetweenData?: boolean;
            extendCursorThroughAxis?: boolean;
            dateRange?: DateRange | null;
            timeZone: UserDisplayPreferences['timeZone'];
            /**
             * Initial expansionBracketTarget, but if you want to show or hide it without rerendering the whole
             * chart, use setExpansionBracketTarget for subsequent updates.
             */
            expansionBracketTarget?: { xTimeScale: d3.ScaleTime<number, number, never> };
            onSelectData?: (data: TimelineDataSlice<Datum, L> | null) => void;
            onSelectTimestamp?: (timestamp: string | null, timeline?: string[]) => void;
            onUserInteraction?: (source: PointerInteractionSource) => void;
        }
    ) {
        const { xAxisKey } = this.options;

        this.expansionBracketTarget = options.expansionBracketTarget;

        this.onSelectTimestamp = (selectedTimestamp) =>
            options.onSelectTimestamp?.(selectedTimestamp, this.allTimestamps);

        this.lines = lines.map((line: L) => {
            const baseDataFilter = line.dataFilter ?? stubTrue;
            const dataFilter = (d: Datum) => baseDataFilter(d) && (!line.valueKey || isFiniteNumber(d[line.valueKey]));
            return {
                ...line,
                dataFilter,
            };
        });

        /*
         * `dataset` is held in ascending `xAxisKey` order, which `dateBisector` and every hover and
         * selection lookup depend on.
         *
         * Each datum's instant is taken once and sorted on, so the comparator reads two numbers. A
         * comparator that parsed its operands would parse each datum about log2(n) times over.
         */
        const decorated = dataset.map((datum) => ({
            datum,
            timeMs: new Date(getXValue(datum, xAxisKey)).getTime(),
        }));
        decorated.sort((a, b) => a.timeMs - b.timeMs);
        this.dataset = decorated.map(({ datum }) => datum);

        this.lineData = Object.fromEntries(
            this.lines.map(({ id, dataFilter }) => {
                return [id, filter(this.dataset, dataFilter)];
            })
        );

        /*
         * Sorted and deduplicated here, which is the order `onSelectTimestamp` hands it out in: the
         * navigation timeline is keyed on it, and normalizing a nearly sorted array once is cheaper than
         * the consumer normalizing it per pointer move during a drag.
         */
        this.allTimestamps = sortedUniq(
            Object.values(this.lineData)
                .flatMap((d) => map(d, xAxisKey) as string[])
                .toSorted()
        );
    }

    lineById(lineId: string): L {
        const line = this.lines.find((l) => l.id === lineId);
        if (!line) {
            throw new Error(`Line not found: ${lineId}`);
        }
        return line;
    }

    // https://d3js.org/d3-array/bisect#bisector_left
    // find the index of the data point with matching timestamp
    readonly dateBisector = d3.bisector<Datum, Date>((d) => new Date(getXValue(d, this.options.xAxisKey)));

    setExpansionBracketTarget(value: { xTimeScale: d3.ScaleTime<number, number, never> } | undefined) {
        this.expansionBracketTarget = value;
        if (value && this.selectedTimestamp) {
            // set it again to get an initial bracket render
            this.setSelectedTimestamp(new Date(this.selectedTimestamp));
        } else if (!value) {
            this.renderExpansionBracket(null);
        }
    }

    destroy() {
        this.draggedTimestamp.cancel();
        this.pointerGesture.cancel();

        const listeningRect = this.rendered?.dynamicElements?.listeningRect;
        listeningRect?.on('mousemove', null).on('mouseleave', null).on('mousedown', null).on('click', null);

        this.removeOwnedNodes?.();
        this.removeOwnedNodes = null;

        this.rendered = undefined;
        this.expansionBracketTarget = undefined;
        this.lineData = {};
        // `dataset` is this chart's own sorted copy and the other two are derived from it, so emptying
        // them in place is safe and keeps the fields' declared variance
        this.allTimestamps.length = 0;
        this.dataset.length = 0;
    }

    render(containerId: string) {
        const { dateRange, timeZone, margin } = this.options;
        const { lines, dataset } = this;

        if (!this.dataset) {
            return;
        }

        const rendered = createTimelineTemplate({
            ...this.options,
            lines,
            dataset,
            containerId,
            timeZone,
            dateRange,
            chartMargin: { bottom: 78, ...margin },
        });

        if (!rendered || !rendered.width) {
            return;
        }

        const { addDynamicElements, svg, x } = rendered;

        const timestampWindowMs = x.invert(TIMESTAMP_WINDOW_PIXELS).getTime() - x.invert(0).getTime();
        const relativeAncestor = findRelativeAncestor(svg.node()!);

        const legendSvg = this.renderLegend(containerId, rendered);
        this.rendered = { ...rendered, timestampWindowMs, legendSvg, relativeAncestor };

        const dataLines = [];
        for (let i = 0; i < lines.length; i++) {
            const line = lines[i];

            const dataForChannel = this.lineData[line.id];
            if (!dataForChannel) {
                continue;
            }
            dataLines.push({ line, data: dataForChannel });
        }

        this.renderLines(dataLines.reverse());

        // Add elements that appear on hover and are one for the entire chart
        // This has to be done after the circles and activeLines are created so that they are on top of the activeLines and circles
        this.rendered.dynamicElements = addDynamicElements();
        this.removeOwnedNodes = captureChartTeardown(containerId);

        const { listeningRect } = this.rendered.dynamicElements;
        listeningRect.on('mousemove', (e) => this.onMouseMove(e));
        listeningRect.on('mouseleave', () => this.onMouseLeave());
        listeningRect.on('mousedown', (e) => this.onMouseDown(e));
        listeningRect.on('click', (e) => this.onClick(e));
    }

    renderLegend(containerId: string, { svg, width, height, margin }: RenderTemplateResult) {
        const { legend } = this.options;
        if (!legend) return null;

        const { color } = this.lines[0];
        /*
        Legend is a horizontal gradient that shows the dot opacity scale
        */
        const legendBarLeftMargin = 116;
        const legendHeight = 10;
        const legendWidth = width - legendBarLeftMargin;

        const legendScale = d3.scaleLinear().domain([0, 100]).range([0, legendWidth]);
        const legendAxis = d3
            .axisBottom(legendScale)
            .tickValues([0, 25, 50, 75, 100])
            .tickFormat((v) => `${v}%`);
        const legendSvg = svg
            .append('g')
            .style(
                'transform',
                `translate(${legendBarLeftMargin}px, ${height + margin.bottom - (legendHeight + 20)}px)`
            )
            .style('color', '#777')
            .style('display', this.expansionBracketTarget ? 'none' : 'initial');

        const gradientId = `timeline-legend-gradient-${containerId}`;
        const legendGradient = svg
            .append('defs')
            .append('linearGradient')
            .attr('id', gradientId)
            .attr('x1', '0%')
            .attr('y1', '0%')
            .attr('x2', '100%')
            .attr('y2', '0%');

        d3.range(0, 1.01, 0.01).forEach((t) => {
            legendGradient
                .append('stop')
                .attr('offset', `${t * 100}%`)
                .attr('stop-opacity', t)
                .attr('stop-color', color);
        });

        legendSvg
            .append('rect')
            .attr('width', legendWidth)
            .attr('height', legendHeight + 1)
            .style('fill', `url(#${gradientId})`);

        // legend labels
        const axisGroup = legendSvg.append('g').style('transform', `translate(0, ${legendHeight}px)`).call(legendAxis);
        axisGroup
            .selectAll('text')
            .attr('text-anchor', (v) => (v === 0 ? 'start' : v === 100 ? 'end' : 'center'))
            .style('font-size', '12px')
            .selectAll('.domain')
            .style('display', 'none');
        axisGroup.select('.domain').remove();
        axisGroup.selectAll('.tick line').remove();

        legendSvg
            .append('text')
            .attr('x', -8)
            .attr('y', legendHeight)
            .attr('text-anchor', 'end')
            .attr('fill', 'gray')
            .style('font-size', '14px')
            .text(legend.label);

        return legendSvg;
    }

    renderLines(dataLines: { line: L; data: Datum[] }[]) {
        dataLines.forEach(({ line, data }) => this.renderLine(line, data));
    }

    renderLine(line: L, dataset: Datum[]) {
        if (!this.rendered) return;
        /*
        Things that need to be done for each line:
        - observation interval backdrop
        - create circles
        */

        const { xAxisKey, xAxisEndKey, dateRange, dotHeight = DOT_HEIGHT, bandHeight = dotHeight * 3 } = this.options;
        const { color, valueKey } = line;
        const { svg, x, y } = this.rendered;
        const lineY = y(line.id)!;

        // Add the backdrop indicating the observation period
        const [startDate, endDate] = x.domain();
        const observationIntervals = (line.observationIntervals && [...line.observationIntervals]) ??
            (dateRange && [dateRange]) ?? [{ startDate, endDate }];
        // if the observation intervals are at the limits of the date range, extend them into the margins
        const observationMin = minBy(observationIntervals, 'startDate')?.startDate;
        const observationMax = maxBy(observationIntervals, 'endDate')?.endDate;
        if (
            observationMin &&
            (!dateRange ||
                (differenceInSeconds(observationMin, dateRange.startDate) <= 60 && isBefore(startDate, observationMin)))
        ) {
            observationIntervals.push({ startDate, endDate: observationMin });
        }
        if (
            observationMax &&
            (!dateRange ||
                (differenceInSeconds(dateRange.endDate, observationMax) <= 60 && isBefore(observationMax, endDate)))
        ) {
            observationIntervals.push({ startDate: observationMax, endDate });
        }
        svg.append('g')
            .selectAll('backdrop')
            .data(observationIntervals)
            .enter()
            .append('rect')
            .attr('width', ({ startDate, endDate }) => x(endDate) - x(startDate))
            .attr('height', bandHeight)
            .attr('x', ({ startDate }) => x(startDate))
            .attr('y', lineY - bandHeight / 2)
            .style('fill', color)
            .style('opacity', 0.1);

        /*
         * A binary span carries no value to shade, so a run of spans whose lozenges touch on screen is one
         * shape however many data points are behind it. Coalescing the run bounds the node count by the
         * axis width. `lineData` holds every datum, so hover, selection and timeline navigation still see
         * the whole series.
         */
        const drawnDataset =
            xAxisEndKey && !valueKey ? this.coalesceSpans(dataset, x, dotHeight, xAxisEndKey) : dataset;

        // Add dots for a scatter plot. Dots signify the data points and allow to identify gaps in the data
        const dotTarget = svg.append('g').selectAll('dot').data(drawnDataset).enter();

        const dotCircle = dotTarget
            .append('rect')
            .attr('x', (datum: Datum) => x(new Date(getXValue(datum, xAxisKey))) - dotHeight / 2)
            .attr('y', lineY - dotHeight / 2)
            .attr(
                'width',
                !xAxisEndKey
                    ? dotHeight
                    : (datum: Datum) => {
                          const left = x(new Date(getXValue(datum, xAxisKey)));
                          const right = x(new Date(getXValue(datum, xAxisEndKey)));
                          return dotHeight + Math.max(0, right - left);
                      }
            )
            .attr('height', dotHeight)
            .attr('rx', dotHeight / 2)
            .attr('ry', dotHeight / 2)
            .style('fill', color);

        if (valueKey) {
            dotCircle
                .style('display', (datum) => (isFiniteNumber(datum[valueKey as keyof Datum]) ? 'initial' : 'none'))
                .style('opacity', (datum: Datum) => getYValue(datum, valueKey) / 100);
        }
    }

    /**
     * Returns `dataset` with each group of spans that overlap on screen combined into one span. The combined
     * span starts at the beginning of the group and ends at its furthest endpoint, so it is drawn as the same
     * single lozenge that the overlapping spans would appear to form.
     *
     * `dataset` must be sorted by `xAxisKey` in ascending order, as guaranteed by the constructor. This keeps
     * each group together and allows the scan to compare each span only with the group currently being built.
     * A span's on-screen width includes the `dotHeight / 2` rounded cap at each end, so spans that only meet at
     * their edges are treated as touching.
     */
    private coalesceSpans(
        dataset: Datum[],
        x: d3.ScaleTime<number, number, never>,
        dotHeight: number,
        xAxisEndKey: NonNullable<typeof this.options.xAxisEndKey>
    ): Datum[] {
        const { xAxisKey } = this.options;
        const radius = dotHeight / 2;

        // list of merged groups built so far, in ascending time order
        const runs: { datum: Datum; endValue: string; rightPx: number }[] = [];
        dataset.forEach((datum) => {
            const endValue = getXValue(datum, xAxisEndKey);
            const startPx = x(new Date(getXValue(datum, xAxisKey)));
            const leftPx = startPx - radius;
            const rightPx = Math.max(startPx, x(new Date(endValue))) + radius;
            const lastGroup = runs[runs.length - 1];

            if (lastGroup && leftPx <= lastGroup.rightPx) {
                if (rightPx > lastGroup.rightPx) {
                    lastGroup.rightPx = rightPx;
                    lastGroup.endValue = endValue;
                }
            } else {
                runs.push({ datum, endValue, rightPx });
            }
        });

        // a run of one is handed back as it came, so an uncoalesced series keeps its datum identities
        return runs.map(({ datum, endValue }) =>
            endValue === getXValue(datum, xAxisEndKey) ? datum : { ...datum, [xAxisEndKey]: endValue }
        );
    }

    renderExpansionBracket(timestamp: Date | null) {
        if (!this.rendered?.dynamicElements || !this.expansionBracketTarget?.xTimeScale) return;

        const { drawExpansionBracket, legendSvg } = this.rendered;

        const dateRange = timestamp ? { startDate: timestamp, endDate: addMinutes(timestamp, 1) } : null;
        drawExpansionBracket?.(dateRange, this.expansionBracketTarget.xTimeScale);

        legendSvg?.style('display', dateRange === null ? 'initial' : 'none');
    }

    drawHoverEffects(timestamp: string) {
        if (!this.rendered?.dynamicElements) return;

        const {
            x,
            margin,
            relativeAncestor,
            dynamicElements: { listeningRect, tooltip, hoverLine },
        } = this.rendered;
        const { timeZone } = this.options;

        const listeningRectNode = listeningRect.node();
        if (!listeningRectNode) {
            return;
        }

        const timestampDate = new Date(timestamp);
        const listeningRectDimensions = listeningRectNode.getBoundingClientRect();
        const cursorX = x(timestampDate);

        // Add in the tooltip
        tooltip.html(`<span style="color: #707070">${formatTimestamp(new Date(timestamp), { timeZone })}</span>`);
        positionCursorTooltip({
            tooltip,
            cursorX,
            chartBounds: listeningRectDimensions,
            relativeAncestor,
            margin,
        });
        hoverLine.attr('x1', cursorX).attr('x2', cursorX);

        tooltip.style('visibility', 'visible');
        hoverLine.style('visibility', 'visible');
    }

    hideHoverEffects() {
        if (!this.rendered?.dynamicElements) return;

        const { tooltip, hoverLine } = this.rendered.dynamicElements;
        tooltip.style('visibility', 'hidden');
        hoverLine.style('visibility', 'hidden');
    }

    dataForTimestamp(timestamp: Date) {
        const { xAxisKey } = this.options;
        const selectedData = compact(
            Object.entries(this.lineData).map(([lineId, dataForLine]) => {
                const i = this.dateBisector.center(dataForLine, timestamp);
                const d = dataForLine[i];
                if (
                    d &&
                    Math.abs(differenceInMilliseconds(timestamp, new Date(getXValue(d, xAxisKey)))) <=
                        (this.rendered?.timestampWindowMs ?? 60000)
                ) {
                    return { line: this.lineById(lineId), datum: d };
                }
            })
        );

        return uniq(map(selectedData, 'datum')).map((datum) => ({
            datum,
            lines: map(filter(selectedData, { datum }), 'line'),
        }));
    }

    findTimestampFromMouseEvent(e: MouseEvent) {
        if (!this.rendered) return;

        const { x } = this.rendered;
        const { xAxisKey, selectTimeBetweenData } = this.options;

        const [xCoord] = d3.pointer(e, e.target);
        const timestamp = x.invert(xCoord);

        const timestamps: string[] = [];
        this.dataForTimestamp(timestamp).forEach(({ datum }) => {
            if (datum) {
                timestamps.push(getXValue(datum, xAxisKey));
            }
        });
        if (timestamps.length === 0 && selectTimeBetweenData) {
            return timestamp.toISOString();
        }
        // return closest candidate timestamp
        const timestampMillis = timestamp.valueOf();
        return minBy(timestamps, (t) => Math.abs(new Date(t).valueOf() - timestampMillis));
    }

    onMouseMove(e: MouseEvent) {
        if (!this.rendered) return;

        const { svg } = this.rendered;

        const timestamp = this.findTimestampFromMouseEvent(e);
        if (timestamp) {
            svg.style('cursor', 'pointer');
            this.drawHoverEffects(timestamp);
            if (e.buttons & 1) {
                // if primary button is down, drag the selected timestamp along with us
                this.draggedTimestamp.send(timestamp);
                this.pointerGesture.drag(timestamp);
            }
        } else {
            svg.style('cursor', 'default');
            this.hideHoverEffects();
        }
    }

    onMouseDown(e: MouseEvent) {
        this.draggedTimestamp.cancel();
        this.maybeClearingSelectedTimestamp = null;
        const timestamp = this.findTimestampFromMouseEvent(e);
        if (timestamp) {
            const isOnSelection = timestamp === this.selectedTimestamp?.toISOString();
            this.pointerGesture.press(timestamp, { isOnSelection });
            if (isOnSelection) {
                this.maybeClearingSelectedTimestamp = timestamp;
            } else {
                this.onSelectTimestamp(timestamp);
            }
        }
    }

    onClick(e: MouseEvent) {
        const timestamp = this.findTimestampFromMouseEvent(e);
        if (
            timestamp &&
            timestamp === this.maybeClearingSelectedTimestamp &&
            timestamp === this.selectedTimestamp?.toISOString()
        ) {
            this.onSelectTimestamp(null);
        }
    }

    onMouseLeave() {
        this.hideHoverEffects();
    }

    clearSelectedTimestamp() {
        this.selectedTimestamp = null;
        this.rendered?.dynamicElements?.selectionLine.style('visibility', 'hidden');
        this.options.onSelectData?.(null);
    }

    setSelectedTimestamp(timestamp: Date | null) {
        if (!this.rendered?.dynamicElements) return;

        if (!timestamp) {
            this.clearSelectedTimestamp();
            return;
        }

        const { selectTimeBetweenData } = this.options;
        const {
            x,
            dynamicElements: { selectionLine },
        } = this.rendered;

        const selectedData = this.dataForTimestamp(timestamp);

        if (!selectedData.length && !selectTimeBetweenData) {
            this.clearSelectedTimestamp();
            return;
        }

        if (this.expansionBracketTarget) {
            this.renderExpansionBracket?.(timestamp);
        }
        this.selectedTimestamp = timestamp;
        const xCoord = x(timestamp);
        selectionLine.style('visibility', 'visible').attr('x1', xCoord).attr('x2', xCoord);

        this.options.onSelectData?.(selectedData);
    }
}

export const drawTimelineChart = <D extends GenericObject, L extends Timeline<D> = Timeline<D>>({
    containerId,
    dataset,
    lines,
    ...options
}: ConstructorParameters<typeof TimelineChart<D, L>>[2] & { containerId: string; dataset: D[]; lines: L[] }) => {
    const lineChart = new TimelineChart(dataset, lines, options);
    lineChart.render(containerId);
    return lineChart;
};
